/**
 * 简体中文 — release-channel
 *
 * 全部 view 已迁移。剩余的内联字符串属于：(a) 透传的后端枚举
 * (channel 名 / quality 等级)，或 (b) 跨语言 DevOps 简写
 * (30d / 1y 等时间窗口 pill、CSV 列名)。
 */
export default {
  app: {
    name: '发布通道',
  },
  nav: {
    dashboard: '仪表盘',
    software: '软件',
    channels: '通道',
    trains: '发布列车',
  },
  navGroups: {
    browse: '浏览',
    manage: '管理',
  },
  auth: {
    signIn: '登录',
    signOut: '退出登录',
    adjustRole: '调整角色',
    signInUnavailable: '当前无法登录, 请联系管理员.',
  },
  user: {
    profile: '个人资料',
  },
  sidebar: {
    shortcuts: '快捷键',
    settings: '设置',
    settingsNotImpl: '设置页尚未实现。',
    collapse: '折叠',
    expand: '展开侧边栏',
    collapseLong: '折叠侧边栏 (仅显示图标)',
    skipLink: '跳到主要内容',
    accountMenu: '账号菜单',
    utilityMenu: '更多选项',
    more: '更多',
    language: '语言',
  },
  lang: {
    title: '语言',
    'zh-CN': '简体中文',
    'en-US': 'English',
  },
  common: {
    loading: '加载中…',
    retry: '重试',
    retrying: '重试中…',
  },
  a11y: {
    primaryNav: '主导航',
    sectionsNav: '分区',
    pageContent: '页面内容',
    notifications: '通知',
    dismissNotification: '关闭通知',
  },
  shortcuts: {
    title: '键盘快捷键',
    escToClose: '按 {esc} 关闭。',
    chordSeparator: '然后',
    close: '关闭',
    groups: {
      navigate: '导航',
      onPage: '本页',
    },
    items: {
      goDashboard: '前往仪表盘',
      goSoftware: '前往软件',
      goChannels: '前往通道',
      focusFilter: '聚焦过滤输入框',
      openHelp: '打开此快捷键帮助',
      closeDialog: '关闭对话框 / 取消',
    },
  },
  cycleGantt: {
    loading: '加载周期日历中…',
    emptyDays: '最近 {days} 天内无晋升记录。',
    srCaption: '最近 {days} 天的通道占位,跨 {count} 个版本每通道一行。',
    srColVersion: '版本',
    srColChannel: '通道',
    srColStart: '开始',
    srColEnd: '结束',
    srColStatus: '状态',
  },
  dashboard: {
    title: '仪表盘',
    subtitle: '所有软件产品的发布通道总览。',
    refresh: '刷新',
    refreshTitleIdle: '重新拉取仪表盘汇总。',
    updatedAgoTitleIdle: '点击「刷新」重新拉取。',
    updatedAgo: '更新于 {ago}',
    stats: {
      software: '软件',
      releases: '发布',
      releasesByChannel: '按通道分布',
      channelTooltipOne: '{channel} 通道上 {count} 个发布',
      channelTooltipOther: '{channel} 通道上 {count} 个发布',
    },
    pipeline: {
      title: '发布流水线',
      pickSoftware: '请选择软件…',
      selectAria: '为发布流水线视图选择软件',
      empty: '尚未注册软件 — 创建后即可查看其发布周期。',
      windowLabel: '时间窗口：',
      windowGroup: '周期甘特图时间窗口',
    },
    promotions: {
      title: '近期晋升',
      empty: '近期窗口内无晋升记录。当发布在 devel → beta → stable 间流转时, 晋升记录会出现在此。',
      by: '由 {user} 操作',
    },
    releases: {
      title: '近期发布',
      empty: '尚未注册发布。新发布版本在 publisher 确认上传后会立即出现在此。',
    },
  },
  software: {
    title: '软件',
    subtitle: '产品列表及各通道当前版本，来源于 Passgate 镜像。新增软件请到 Passgate 操作，然后点击「刷新」拉取。',
    refresh: '刷新',
    refreshing: '刷新中…',
    refreshTitleBusy: '正在从 Passgate 刷新…',
    refreshTitleIdle: '从 Passgate 拉取最新应用列表',
    refreshingAria: '正在从 Passgate 刷新软件列表。',
    emptyTitle: 'Passgate 中暂未注册任何软件。',
    emptyHint: '请先在 Passgate 添加产品，然后点击「刷新」。',
    filter: {
      ariaText: '过滤软件',
      placeholderText: '按名称、ID、描述或仓库过滤…  (按 / 聚焦)',
      channelAria: '按通道过滤',
      channelAll: '全部通道',
      channelCount: '{count} 个通道',
      channelNone: '暂无通道',
      activityAria: '按最近活动过滤',
      clear: '清除过滤',
      emptyMatch: '当前过滤条件下没有匹配的软件。',
    },
    tree: {
      all: '全部应用',
      ungrouped: '未分组',
    },
    activity: {
      any: '不限活动',
      recent7: '7 天内有发布',
      recent30: '30 天内有发布',
      stale90: '90 天内无发布',
      never: '从未发布',
    },
    showingOne: '显示 {filtered} / {total} 个软件',
    showingOther: '显示 {filtered} / {total} 个软件',
    filteredSuffix: '(已过滤)',
    export: '导出 CSV',
    exportTitleEmpty: '无可导出 — 当前过滤无匹配。',
    exportTitle: '将 {count} 个软件导出为 CSV (每个通道指针一列)。',
    toastExportedOne: '已将 {count} 个软件导出为 {filename}。',
    toastExportedOther: '已将 {count} 个软件导出为 {filename}。',
    toastRefreshedOne: '软件列表已刷新；{count} 条。',
    toastRefreshedOther: '软件列表已刷新；{count} 条。',
    errorLoad: '软件列表加载失败',
    errorRefreshWithMsg: '刷新失败：{msg}。继续显示缓存列表。',
    errorRefresh: '刷新失败；继续显示缓存列表。',
    retiredBadge: '已退休',
  },
  softwareDetail: {
    back: '全部软件',
    promoteDisabledReason: '尚无可晋升的通道指针 — 先注册一条发布。',
    quickLinks: {
      repository: '代码仓库',
      passgateLabel: 'Passgate',
      passgateTitle: 'Passgate 应用 ID — 控制此软件的发布 ACL。',
    },
    lifecycle: {
      retiredBadge: '已退休',
      retire: '退休',
      retiring: '退休中…',
      unretire: '恢复',
      unretiring: '恢复中…',
      retirePrompt: '退休原因（可选）：',
      retireConfirm: '退休 {name}？此后所有写操作（注册 / 晋升 / ACL）都会被拒（409），daily train tick 暂停。读取仍可用，之后可恢复。',
      retireToast: '已退休 {name}。',
      unretireToast: '已将 {name} 恢复为 active。',
      error: '{verb}失败：{msg}',
      errorElevate: '退休 / 恢复仅限 owner。请先 step-up：{cmd}',
      retiredBannerTitle: '此软件已退休。',
      retiredBannerBody: '所有写操作被拒（409），daily tick 暂停；读取仍可用。',
    },
    pointers: {
      title: '通道指针',
      emptyBefore: '尚无通道指针。请用上方 Register（或',
      emptyAfter: '）初始化首个通道。',
      internalToggle: '内部通道 ({count})',
    },
    calendar: {
      title: '通道日历',
      subtitle: '最近 {days} 天内 devel → beta → stable 的版本活动，由历史记录推导。',
      empty: '最近 42 天无发布活动 — 下次注册或晋升后日历会自动填充。',
    },
    history: {
      title: '近期发布',
      countSuffixOne: '最近 {count} 条，最新在前',
      countSuffixOther: '最近 {count} 条，最新在前',
      emptyBefore: '尚未注册任何发布。请用上方 Register，或用 CLI',
      emptyAfter: '注册清单。',
      colExpand: '详情',
      expandAria: '展开/收起 {version} 的 artifact 与审计',
      colVersion: '版本',
      colChannel: '通道',
      colQuality: '质量',
      colCommit: '提交',
      colRegistered: '注册时间',
      colBy: '操作人',
    },
  },
  releaseDetail: {
    artifacts: 'Artifacts',
    artifactsEmpty: '此发布没有记录任何 artifact。',
    colKind: '类型',
    colFilename: '文件名',
    colBackend: '后端',
    colSize: '大小',
    colSha: 'sha256',
    colState: '状态',
    provenance: '溯源',
    builder: '构建者',
    builtAt: '构建于',
    sbom: 'SBOM',
    attestation: 'Attestation',
    dependsOn: '依赖',
    anyVersion: '任意',
    audit: '审计轨',
    auditError: '审计加载失败：{msg}',
    auditEmpty: '无审计事件。',
    evRegister: '注册',
    evPromote: '晋升',
    ciJob: 'CI job ↗',
  },
  channels: {
    title: '通道',
    subtitle: '三通道发布流水线：Devel → Beta → Stable。',
    fallbackBanner: '{msg} 已显示默认流水线。',
    level: '稳定度 {n}',
    deployed: '当前部署',
    emptyForChannel: '{ch} 通道尚未晋升任何版本。',
    csvButton: 'CSV',
    csvTitle: '将 {ch} 通道的全部 {count} 条发布导出为 CSV。',
    csvAria: '导出 {ch} 通道为 CSV',
    toastExportedOne: '已将 {ch} 的 {count} 条发布导出为 {filename}。',
    toastExportedOther: '已将 {ch} 的 {count} 条发布导出为 {filename}。',
    fallback: {
      devel: '最早期构建 — 由 CI 自动产出',
      beta: '经过更长测试后从 Devel 晋升',
      stable: '生产可用 — 从 Beta 晋升',
    },
  },
  trains: {
    resource: '发布列车',
    title: '发布列车',
    subtitle: '公司级 canary 节拍：daily-cron 每次 tick 给所有 active 软件自动 bump Y；New cycle 推进共享大版本 X。',
    refresh: '刷新',
    newTrain: '新建列车',
    createTitle: '新建发布列车',
    fieldName: '名称',
    namePlaceholder: 'company',
    fieldAnchor: '锚定日期',
    fieldCron: 'Daily cron',
    fieldTimezone: '时区',
    fieldMajor: '当前大版本 (X)',
    cancel: '取消',
    create: '创建',
    creating: '创建中…',
    empty: '尚无发布列车。创建一条来驱动每日 canary 节拍。',
    cycleLabel: '周期 {x}',
    disabledBadge: '已停用',
    anchor: '锚定 {date}',
    updated: '更新于 {ago}',
    enable: '启用',
    disable: '停用',
    newCycle: '开新周期',
    newCycleTitle: '推进到下一个大版本（X+1），active 软件重置到 X.0.0。',
    newCycleConfirm: '在 {name} 上开新周期？current_major → {next}。active 软件下一个 canary 从 X.0.0 起步。',
    tick: '立即 tick',
    tickTitle: '立刻触发每日 canary bump（所有 active 软件 Y++）。',
    tickDisabledTitle: '列车已停用 — 启用后才能 tick。',
    tickConfirm: '现在触发 {name} 的每日 tick？所有 active 软件会 canary Y++。',
    cronLabel: 'Daily cron',
    timezoneLabel: '时区',
    saveSchedule: '保存',
    saving: '保存中…',
    cronHint: '列车时区下的标准 5 段 cron。保存后调度器在下次 sync 重新注册，无需重启。',
    bindingsToggle: '软件绑定',
    bindingsEmpty: '尚无软件绑定到此列车。',
    pickSoftware: '添加软件…',
    addBinding: '添加',
    part: {
      active: 'active',
      frozen: 'frozen',
      archived: 'archived',
    },
    toastScheduleSaved: '已保存 {name} 的调度。',
    toastEnabled: '已启用 {name}。',
    toastDisabled: '已停用 {name}。',
    toastNewCycle: '已在 {name} 开启周期 {major}。',
    toastTick: '已 tick {name}：bump {bumped} 个，跳过 {skipped} 个。',
    toastTickNone: '已 tick {name}：无可 bump。',
    toastBinding: '已设置 {name} → {p}。',
    toastCreated: '已创建列车 {name}。',
    errorAction: '操作失败：{msg}',
    readOnlyTitle: '需要平台 sysadmin 角色。',
    readOnlyBanner: '只读：列车管理需要平台 sysadmin 角色。你可以查看节拍与绑定,但创建、编辑、tick、改绑定均已禁用。',
  },
  calendar: {
    title: '通道日历',
    previewBanner: '预览：日历后端 (/api/v1/calendar) 尚未实现,当前展示 mock 数据;接口上线后会切回真实视图。',
    subtitlePrefix: '三通道 × 多发布时间线 ·',
    subtitleDaysWord: '天',
    subtitleStarting: '起始',
    prevWeek: '上一周',
    today: '今天',
    nextWeek: '下一周',
    prevWeekLabel: '‹ 1w',
    todayLabel: '今天',
    nextWeekLabel: '1w ›',
    windowAria: '窗口大小',
    weeks4: '4 周',
    weeks6: '6 周',
    weeks8: '8 周',
    weeks12: '12 周',
  },
  promote: {
    button: '晋升…',
    dialogTitle: '晋升发布',
    dialogSubtitle: '将发布在通道阶梯上前进一步。只允许向前、每次一跳。',
    fromLabel: '源通道',
    toLabel: '目标通道',
    toAuto: '自动：{to}',
    toAtTop: '已是顶端通道 — 无可晋升。',
    internalNote: 'test / canary 是内部通道,只在 hot-patch / replay 等运维场景出现。',
    sourceHeading: '源发布',
    sourceLoading: '正在加载源发布…',
    sourceEmpty: '{channel} 通道上没有发布 — 无可晋升。',
    sourceError: '源发布加载失败：{msg}',
    sourceVersion: '版本',
    sourceCommit: '提交',
    sourceRegistered: '注册时间',
    sourceRegisteredBy: '操作人',
    sourceQuality: '质量',
    qualityWarningBroken: '质量等级为 {q}。后端仍允许晋升，但损坏的发布不应轻易前推 — 先与发布 owner 确认。',
    qualityWarningUnreviewed: '质量等级为 {q} — calendar daemon 尚未出具结论。可以照样晋升，但建议等到绑定绿色后再动。',
    versionLabel: '目标版本（X.Y.Z）',
    versionHint: '仅在 test → canary 晋升时需要：canary 发布需要真实的 semver，而不是源 commit 的7位 short sha。',
    versionWhyTestToCanary: '为什么这里要填版本号：test 通道把 commit short SHA 当版本字段记录,canary 起需要正式的 X.Y.Z;一次性指定后,后续 promote 会沿用这个版本号。',
    versionPlaceholder: '1.2.3',
    versionInvalid: '必须为 X.Y.Z（仅数字，两个点）。',
    reasonLabel: '原因（可选）',
    reasonPlaceholder: '简短审计说明 — 为何现在晋升？',
    submit: '晋升',
    submitting: '晋升中…',
    cancel: '取消',
    toastSuccess: '已将 {software} 从 {from} 晋升到 {to}。',
    error400: '{msg}',
    error403: '您没有晋升到 {to} 的权限。',
    error404: '{from} 通道未找到发布 — 无可晋升。',
    error409: '冲突 — 源通道指针在您打开对话框后发生了变化。请重新打开。',
    errorGeneric: '晋升失败：{msg}',
    elevateTitle: '需要 step-up 到 {role} 角色',
    elevateBody: '该通道需要 {required}；你当前会话是 {current}。可用账户菜单的“调整角色”切换会话后重试，或为 CLI / API 路径换一个一次性令牌：',
    elevateThen: 'elevated 令牌有效约 1 小时；后端仅在你的 passgate UserAppRole 允许时才发放。',
  },
  register: {
    button: '注册发布…',
    sectionNavAria: '表单分节导航',
    requiredCount: '{n} 必填',
    pageTitle: '注册发布',
    pageSubtitle: '为 {software} 手动录入一份多 artifact 的发布 manifest。常规由 CI 驱动 — 本页面适用于 hot-patch、重放、一次性修正场景。',
    backToDetail: '← 返回详情',
    section: {
      basics: '基本信息',
      artifacts: 'Artifacts',
      provenance: 'Provenance（可选）',
      dependsOn: '依赖（可选）',
      audit: '审计（可选）',
    },
    commitShaLabel: 'Commit SHA（40位 hex）',
    commitShaPlaceholder: 'a1b2c3…（完整 40 位小写 hex）',
    commitShaInvalid: '必须是恰好 40 位小写 hex 字符。',
    versionLabel: '版本',
    versionPlaceholderCanary: 'X.Y.Z（留空会使用 0.0.0+g 哨兵值）',
    versionPlaceholderTest: '7位 commit short sha（例如 {short}）',
    versionInvalidTest: 'test 通道上版本必须为 7 位 commit short sha。',
    versionInvalidCanary: '设定后版本必须为 X.Y.Z（仅数字）。',
    channelLabel: '通道',
    channelHintTest: 'test — test/* 分支的 push-time CI build。版本 ＝ 7 位 short sha。',
    channelHintCanary: 'canary — 带版本号的预发布。通常通过 /version/reserve 预留版本。',
    artifactsHint: '至少需要一个 artifact，每个必须填写 kind、filename、location_type 与 location_uri。',
    artifactKind: '类型',
    artifactFilename: '文件名',
    artifactLocationType: '后端',
    artifactLocationUri: 'URI',
    artifactSha256: 'sha256（可选）',
    artifactSizeBytes: '字节数（可选）',
    artifactContentType: 'content_type（可选）',
    artifactAdd: '+ 添加 artifact',
    artifactRemove: '移除',
    provenanceSbomRef: 'SBOM ref',
    provenanceAttestationRef: 'Attestation ref',
    provenanceBuiltAt: '构建时间',
    provenanceBuilder: '构建者',
    provenanceSidecarRefs: 'Sidecar refs（每行一个）',
    dependsOnRelease: '发布 ID',
    dependsOnVersion: '版本（可选）',
    dependsOnChannel: '通道',
    dependsOnConstraint: 'Constraint（可选）',
    dependsOnAdd: '+ 添加依赖',
    dependsOnRemove: '移除',
    auditRegisteredBy: 'registered_by（覆盖）',
    auditRegisteredByHint: '留空则使用您的 passgate 身份。',
    auditBuilder: '构建者',
    auditCiJobUrl: 'CI 作业 URL',
    auditNote: '备注',
    submit: '注册',
    submitting: '注册中…',
    cancel: '取消',
    toastSuccess: '已在 {channel} 通道注册 {software} v{version}。',
    toastSuccessNoVersion: '已在 {channel} 通道注册 {software}。',
    error400: '{msg}',
    error403: '您没有为 {software} 注册发布的权限。',
    error409: '该 commit + 通道的发布已存在。',
    errorGeneric: '注册失败：{msg}',
    needsArtifact: '提交前请至少添加一个 artifact。',
  },
}
